import { useEffect, useMemo, useState } from 'react'
import { Zap } from 'lucide-react'

import { traceService } from '../services/traceService'
import InvariantSamplesPanel from './InvariantSamplesPanel'
import { invariantIdsOf } from '../utils/invariantIds'

export type ObservabilityAlertStatus = 'active' | 'acknowledged' | 'resolved'
export type ObservabilityAlertSeverity = 'critical' | 'warning' | 'info'

export type ObservabilityAlert = {
  id: string
  severity: ObservabilityAlertSeverity
  type: string
  message: string
  tenantId: string
  status: ObservabilityAlertStatus
  firstSeenAt: string
  lastSeenAt: string
  affectedConversations: number
  metadata?: Record<string, unknown>
}

/**
 * Simulación determinista del supervisor critic para invariantes en modo warn:
 * qué hubiera hecho el supervisor si el invariante estuviera en rewrite.
 * NUNCA fue aplicada — es observabilidad what-if para diagnóstico.
 */
export type SimulatedCriticResult = {
  action: 'redirect' | 'proceed' | 'escalate'
  targetNode: string | null
  correctionInstruction: string | null
  selectedProductName: string | null
  reason: string
  source: 'deterministic'
}

const SIMULATION_PANEL_STYLE = {
  marginTop: 8,
  padding: '10px 12px',
  borderRadius: 10,
  border: '1px dashed rgba(245,158,11,0.35)',
  background: 'rgba(245,158,11,0.06)',
  fontSize: 12,
} as const

function SimulatedCriticPanel({ simulated }: { simulated: SimulatedCriticResult }) {
  return (
    <details style={{ marginTop: 8 }}>
      <summary
        style={{
          fontSize: 11,
          color: 'rgba(245,158,11,0.9)',
          cursor: 'pointer',
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <Zap size={12} aria-hidden />
        ¿Qué hubiera hecho el Supervisor en modo rewrite?
      </summary>
      <div style={SIMULATION_PANEL_STYLE}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
          <span style={{ fontWeight: 700, color: '#fde68a' }}>
            Acción: <span style={{ color: '#fbbf24' }}>{simulated.action}</span>
          </span>
          {simulated.targetNode ? (
            <span style={{ fontWeight: 700, color: '#fde68a' }}>
              →{' '}
              <span
                style={{
                  fontFamily: 'ui-monospace, monospace',
                  background: 'rgba(245,158,11,0.15)',
                  padding: '2px 6px',
                  borderRadius: 6,
                  color: '#fcd34d',
                }}
              >
                {simulated.targetNode}
              </span>
            </span>
          ) : null}
          {simulated.selectedProductName ? (
            <span style={{ color: 'rgba(226,232,240,0.75)', fontWeight: 600 }}>
              Producto: {simulated.selectedProductName}
            </span>
          ) : null}
          <span style={{ color: 'rgba(226,232,240,0.5)', fontSize: 11 }}>({simulated.reason})</span>
        </div>
        {simulated.correctionInstruction ? (
          <div
            style={{
              padding: '8px 10px',
              borderRadius: 8,
              background: 'rgba(0,0,0,0.25)',
              border: '1px solid rgba(245,158,11,0.20)',
              color: '#fde68a',
              fontStyle: 'italic',
              lineHeight: 1.55,
            }}
          >
            "{simulated.correctionInstruction}"
          </div>
        ) : null}
        <div style={{ marginTop: 6, fontSize: 10, color: 'rgba(226,232,240,0.4)' }}>
          Simulación determinista — no fue aplicada (invariante en modo warn)
        </div>
      </div>
    </details>
  )
}

function pillStyle(severity: ObservabilityAlertSeverity) {
  switch (severity) {
    case 'critical':
      return { background: 'rgba(239,68,68,0.14)', border: '1px solid rgba(239,68,68,0.35)', color: '#fecaca' }
    case 'warning':
      return { background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.32)', color: '#fde68a' }
    default:
      return { background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.30)', color: '#bfdbfe' }
  }
}

function statusStyle(status: ObservabilityAlertStatus) {
  switch (status) {
    case 'active':
      return { background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.30)', color: '#bbf7d0' }
    case 'acknowledged':
      return { background: 'rgba(148,163,184,0.12)', border: '1px solid rgba(148,163,184,0.30)', color: '#e2e8f0' }
    default:
      return { background: 'rgba(100,116,139,0.10)', border: '1px solid rgba(100,116,139,0.22)', color: 'rgba(226,232,240,0.7)' }
  }
}

function formatWhen(value: string) {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

export function ObservabilityAlertsView({
  tenantId,
  alerts,
  loading,
  error,
  status,
  onStatusChange,
  onAck,
  onResolve,
  onRefresh,
}: {
  tenantId: string
  alerts: ObservabilityAlert[]
  loading: boolean
  error: string
  status: ObservabilityAlertStatus | 'all'
  onStatusChange: (value: ObservabilityAlertStatus | 'all') => void
  onAck: (id: string) => void
  onResolve: (id: string) => void
  onRefresh: () => void
}) {
  const emptyLabel = !tenantId
    ? 'Selecciona un tenant para ver alertas.'
    : loading
      ? 'Cargando alertas...'
      : error
        ? error
        : 'No hay alertas en esta ventana.'

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 800 }}>Alertas</div>
          <div style={{ fontSize: 12, color: 'rgba(226,232,240,0.65)' }}>
            Reglas accionables con estados: active, acknowledged, resolved.
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select
            className="obs-select"
            style={{ width: 160 }}
            value={status}
            onChange={(e) => onStatusChange(e.target.value as any)}
            disabled={!tenantId || loading}
          >
            <option value="all">todas</option>
            <option value="active">active</option>
            <option value="acknowledged">acknowledged</option>
            <option value="resolved">resolved</option>
          </select>
          <button
            onClick={onRefresh}
            disabled={!tenantId || loading}
            style={{
              padding: '8px 10px',
              borderRadius: 10,
              border: '1px solid rgba(255,255,255,0.12)',
              background: 'rgba(255,255,255,0.04)',
              color: '#e2e8f0',
              fontSize: 12,
              fontWeight: 800,
              cursor: !tenantId || loading ? 'not-allowed' : 'pointer',
              opacity: !tenantId || loading ? 0.6 : 1,
            }}
          >
            Refresh
          </button>
        </div>
      </div>

      {alerts.length === 0 ? (
        <div style={{ padding: 14, borderRadius: 12, border: '1px solid rgba(255,255,255,0.10)', background: 'rgba(255,255,255,0.03)' }}>
          {emptyLabel}
        </div>
      ) : (
        <div style={{ border: '1px solid rgba(255,255,255,0.10)', borderRadius: 14, overflow: 'hidden', background: 'rgba(255,255,255,0.02)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '120px 120px 1fr 140px 160px 160px 150px', gap: 0, padding: '10px 12px', borderBottom: '1px solid rgba(255,255,255,0.08)', fontSize: 11, color: 'rgba(226,232,240,0.55)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            <div>Severidad</div>
            <div>Status</div>
            <div>Mensaje</div>
            <div>Afectadas</div>
            <div>First</div>
            <div>Last</div>
            <div>Accion</div>
          </div>

          {alerts.map((a) => (
            <div key={a.id} style={{ display: 'grid', gridTemplateColumns: '120px 120px 1fr 140px 160px 160px 150px', gap: 0, padding: '10px 12px', borderBottom: '1px solid rgba(255,255,255,0.06)', alignItems: 'start' }}>
              <div>
                <span style={{ display: 'inline-flex', padding: '4px 8px', borderRadius: 999, fontSize: 11, fontWeight: 800, ...pillStyle(a.severity) }}>
                  {a.severity}
                </span>
              </div>
              <div>
                <span style={{ display: 'inline-flex', padding: '4px 8px', borderRadius: 999, fontSize: 11, fontWeight: 800, ...statusStyle(a.status) }}>
                  {a.status}
                </span>
              </div>
              <div style={{ fontSize: 12, lineHeight: 1.45 }}>
                <div style={{ fontWeight: 800, color: 'rgba(226,232,240,0.92)' }}>{a.type}</div>
                <div style={{ color: 'rgba(226,232,240,0.75)' }}>{a.message}</div>
                <div style={{ marginTop: 6, fontSize: 11, color: 'rgba(226,232,240,0.45)', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}>
                  {a.id}
                </div>
                {a.type === 'invariant_warn' &&
                (a.metadata as Record<string, unknown> | undefined)?.simulatedCriticResult ? (
                  <SimulatedCriticPanel
                    simulated={(a.metadata as Record<string, unknown>).simulatedCriticResult as SimulatedCriticResult}
                  />
                ) : null}
                {a.type === 'invariant_warn'
                  ? invariantIdsOf(a).map((invariantId) => (
                      <InvariantSamplesPanel key={invariantId} tenantId={a.tenantId} alertId={a.id} invariantId={invariantId} />
                    ))
                  : null}
              </div>
              <div style={{ fontSize: 12, color: 'rgba(226,232,240,0.8)' }}>{a.affectedConversations}</div>
              <div style={{ fontSize: 12, color: 'rgba(226,232,240,0.75)' }}>{formatWhen(a.firstSeenAt)}</div>
              <div style={{ fontSize: 12, color: 'rgba(226,232,240,0.75)' }}>{formatWhen(a.lastSeenAt)}</div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-start', alignItems: 'center' }}>
                {a.status === 'active' ? (
                  <button
                    onClick={() => onAck(a.id)}
                    style={{
                      padding: '6px 10px',
                      borderRadius: 10,
                      border: '1px solid rgba(148,163,184,0.32)',
                      background: 'rgba(148,163,184,0.12)',
                      color: '#e2e8f0',
                      fontSize: 12,
                      fontWeight: 800,
                      cursor: 'pointer',
                    }}
                  >
                    Acknowledge
                  </button>
                ) : null}
                {a.status !== 'resolved' ? (
                  <button
                    onClick={() => onResolve(a.id)}
                    style={{
                      padding: '6px 10px',
                      borderRadius: 10,
                      border: '1px solid rgba(34,197,94,0.35)',
                      background: 'rgba(34,197,94,0.16)',
                      color: '#bbf7d0',
                      fontSize: 12,
                      fontWeight: 800,
                      cursor: 'pointer',
                    }}
                  >
                    Resolve
                  </button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default function DebugObservabilityAlerts({
  tenantId,
  minutes = 1440,
}: {
  tenantId: string
  minutes?: number
}) {
  const [alerts, setAlerts] = useState<ObservabilityAlert[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [status, setStatus] = useState<ObservabilityAlertStatus | 'all'>('active')

  const refresh = async () => {
    if (!tenantId) return
    setLoading(true)
    setError('')
    try {
      const res = await traceService.getObservabilityAlerts({
        tenantId,
        status: status === 'all' ? undefined : status,
        minutes,
      })
      setAlerts(res.data ?? [])
    } catch {
      setAlerts([])
      setError('No se pudieron cargar las alertas')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void refresh()
  }, [tenantId, status, minutes])

  const ack = async (id: string) => {
    if (!tenantId) return
    try {
      await traceService.ackObservabilityAlert({ tenantId, alertId: id })
      void refresh()
    } catch {
      // keep silent; operator can refresh
    }
  }

  const resolve = async (id: string) => {
    if (!tenantId) return
    try {
      await traceService.resolveObservabilityAlert({ tenantId, alertId: id })
      void refresh()
    } catch {
      // keep silent; operator can refresh
    }
  }

  const sorted = useMemo(() => {
    return [...alerts].sort((a, b) => {
      return new Date(b.lastSeenAt).getTime() - new Date(a.lastSeenAt).getTime()
    })
  }, [alerts])

  return (
    <ObservabilityAlertsView
      tenantId={tenantId}
      alerts={sorted}
      loading={loading}
      error={error}
      status={status}
      onStatusChange={setStatus}
      onAck={ack}
      onResolve={resolve}
      onRefresh={refresh}
    />
  )
}

